/**
 * Widget management composable
 */

import { ref, computed, watch, onMounted } from 'vue';
import { WidgetType } from '@awesomeclocks/types';
import type {
  WidgetConfig,
  DashboardConfig,
  CreateWidgetPayload,
  ClockWidgetConfig,
  WorldClockWidgetConfig,
  TimerWidgetConfig,
  StopwatchWidgetConfig,
  AlarmWidgetConfig,
  WidgetSize,
} from '@awesomeclocks/types';
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@awesomeclocks/utils';

const DEFAULT_DASHBOARD_CONFIG: DashboardConfig = {
  widgets: [],
  layout: {
    cols: 12,
    rowHeight: 60,
    margin: [10, 10],
  },
  theme: 'auto',
};

const DEFAULT_WIDGET_SIZES: Record<WidgetType, WidgetSize> = {
  [WidgetType.CLOCK]: { w: 4, h: 8 },
  [WidgetType.WORLD_CLOCK]: { w: 4, h: 8 },
  [WidgetType.TIMER]: { w: 4, h: 8 },
  [WidgetType.STOPWATCH]: { w: 4, h: 8 },
  [WidgetType.ALARM]: { w: 4, h: 8 },
};

const getDefaultWidgetSize = (type: WidgetType): WidgetSize => {
  return DEFAULT_WIDGET_SIZES[type] ?? { w: 4, h: 5 };
};

const normalizeWidgetPositions = (config: DashboardConfig) => {
  let updated = false;

  const normalizedWidgets = config.widgets.map((widget) => {
    const defaultSize = getDefaultWidgetSize(widget.type);
    const position = { ...widget.position };

    if (position.w < defaultSize.w) {
      position.w = defaultSize.w;
      updated = true;
    }

    if (position.h < defaultSize.h) {
      position.h = defaultSize.h;
      updated = true;
    }

    return updated ? { ...widget, position } : widget;
  });

  if (updated) {
    config.widgets = normalizedWidgets;
  }
};

export function useWidgets() {
  const dashboardConfig = ref<DashboardConfig>(DEFAULT_DASHBOARD_CONFIG);
  const widgets = computed(() => dashboardConfig.value.widgets);

  // Load dashboard config from localStorage
  const loadDashboard = () => {
    const saved = getStorageItem<DashboardConfig>(STORAGE_KEYS.DASHBOARD_CONFIG);
    if (saved) {
      normalizeWidgetPositions(saved);
      dashboardConfig.value = saved;
    }
  };

  // Save dashboard config to localStorage
  const saveDashboard = () => {
    setStorageItem(STORAGE_KEYS.DASHBOARD_CONFIG, dashboardConfig.value);
  };

  // Auto-save on changes
  watch(dashboardConfig, saveDashboard, { deep: true });

  // Create a new widget with default settings
  const createWidget = (payload: CreateWidgetPayload): WidgetConfig => {
    const id = `widget-${Date.now()}`;
    const defaultSize = getDefaultWidgetSize(payload.type);
    const baseConfig = {
      id,
      name: payload.name,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      position: {
        x: 0,
        y: 0,
        w: defaultSize.w,
        h: defaultSize.h,
        ...(payload.position ?? {}),
      },
    };

    switch (payload.type) {
      case 'clock':
        return {
          ...baseConfig,
          type: 'clock' as const,
          settings: {
            format: '24h',
            showSeconds: true,
            showDate: true,
            displayType: 'digital',
          },
        } as ClockWidgetConfig;

      case 'world-clock':
        return {
          ...baseConfig,
          type: 'world-clock' as const,
          settings: {
            timezones: [],
            format: '24h',
            showSeconds: true,
          },
        } as WorldClockWidgetConfig;

      case 'timer':
        return {
          ...baseConfig,
          type: 'timer' as const,
          settings: {
            duration: 300, // 5 minutes default
            autoStart: false,
          },
          state: {
            remaining: 300,
            isRunning: false,
            isPaused: false,
          },
        } as TimerWidgetConfig;

      case 'stopwatch':
        return {
          ...baseConfig,
          type: 'stopwatch' as const,
          settings: {
            showMilliseconds: true,
            lapRecording: true,
          },
          state: {
            elapsed: 0,
            isRunning: false,
            laps: [],
          },
        } as StopwatchWidgetConfig;

      case 'alarm':
        return {
          ...baseConfig,
          type: 'alarm' as const,
          settings: {
            alarms: [],
          },
        } as AlarmWidgetConfig;

      default:
        throw new Error(`Unknown widget type: ${payload.type}`);
    }
  };

  // Add a new widget to the dashboard
  const addWidget = (payload: CreateWidgetPayload) => {
    const defaultSize = getDefaultWidgetSize(payload.type);
    // Calculate position for new widget (simple algorithm: place below existing widgets)
    const maxY = dashboardConfig.value.widgets.reduce((max, w) => {
      const bottom = w.position.y + w.position.h;
      return bottom > max ? bottom : max;
    }, 0);

    const widget = createWidget({
      ...payload,
      position: {
        x: 0,
        y: maxY,
        w: defaultSize.w,
        h: defaultSize.h,
      },
    });
    dashboardConfig.value.widgets.push(widget);
  };

  // Remove a widget by ID
  const removeWidget = (id: string) => {
    dashboardConfig.value.widgets = dashboardConfig.value.widgets.filter(
      (w) => w.id !== id
    );
  };

  // Update widget name
  const updateWidgetName = (id: string, name: string) => {
    const widget = dashboardConfig.value.widgets.find((w) => w.id === id);
    if (widget) {
      widget.name = name;
      widget.updatedAt = Date.now();
    }
  };

  // Update widget position
  const updateWidgetPosition = (id: string, position: WidgetConfig['position']) => {
    const widget = dashboardConfig.value.widgets.find((w) => w.id === id);
    if (widget) {
      widget.position = position;
      widget.updatedAt = Date.now();
    }
  };

  // Update widget settings (type-specific)
  const updateWidgetSettings = (id: string, settings: Record<string, unknown>) => {
    const widget = dashboardConfig.value.widgets.find((w) => w.id === id);
    if (widget) {
      // @ts-expect-error - settings type varies by widget type
      widget.settings = settings;
      widget.updatedAt = Date.now();
    }
  };

  // Update widget state (for timer/stopwatch)
  const updateWidgetState = (id: string, state: Record<string, unknown>) => {
    const widget = dashboardConfig.value.widgets.find((w) => w.id === id);
    if (widget && 'state' in widget) {
      // @ts-expect-error - state type varies by widget type
      widget.state = state;
      widget.updatedAt = Date.now();
    }
  };

  // Update theme
  const updateTheme = (theme: DashboardConfig['theme']) => {
    dashboardConfig.value.theme = theme;
  };

  // Clear all widgets
  const clearAllWidgets = () => {
    dashboardConfig.value.widgets = [];
  };

  // Initialize
  onMounted(() => {
    loadDashboard();
  });

  return {
    dashboardConfig,
    widgets,
    addWidget,
    removeWidget,
    updateWidgetName,
    updateWidgetPosition,
    updateWidgetSettings,
    updateWidgetState,
    updateTheme,
    clearAllWidgets,
    loadDashboard,
    saveDashboard,
  };
}
